import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiUsers } from 'react-icons/hi';
import { useGeneral } from '../../contexts';
import { useChannels } from '../../hooks/useChannels';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import ChannelInitialIcon from '../../components/ui/ChannelInitialIcon/ChannelInitialIcon';
import { createSlug } from '../../utils/urlSlug';
import { ChannelListItem } from '../../types/channel.types';
import styles from './AdminChannelsPage.module.css';

interface AdminChannelListItemProps {
  channel: ChannelListItem;
  channelTitle: string;
  channelLogo: string | null;
  hasLogo: boolean;
  onClick: () => void;
}

const AdminChannelListItem: React.FC<AdminChannelListItemProps> = ({
  channel,
  channelTitle,
  channelLogo,
  hasLogo,
  onClick
}) => {
  const { language } = useGeneral();
  const { getTranslation } = language;
  const [imageLoading, setImageLoading] = useState(hasLogo);
  const memberCount = channel.userCount || channel.user_count || channel.memberCount || 0;
  const isPublic = channel.isPublic === "1";

  return (
    <div
      className={styles.listItem}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.listItemLeft}>
        <div className={styles.listItemIcon}>
          {hasLogo ? (
            <>
              {imageLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '50%' }} />}
              <img
                src={channelLogo || ''}
                alt={channelTitle}
                className={styles.iconImage}
                onLoad={() => setImageLoading(false)}
                onError={() => {
                  setImageLoading(false);
                }}
              />
            </>
          ) : (
            <ChannelInitialIcon channelName={channelTitle || ''} className={styles.iconImage} />
          )}
        </div>
        <div className={styles.listItemText}>
          <div className={styles.listItemTitle}>{channelTitle}</div>
          <div className={styles.listItemStatus}>
            {isPublic 
              ? (getTranslation('organizerPanel.public') || 'Herkese Açık')
              : (getTranslation('organizerPanel.private') || 'Özel')
            }
          </div>
        </div>
      </div>
      <button className={styles.memberCountButton}>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
        {/* @ts-ignore - react-icons compatibility with React 19 */}
        <HiUsers size={16} />
        <span>{memberCount}</span>
      </button>
    </div>
  );
};

const AdminChannelsPage: React.FC = () => {
  const navigate = useNavigate();
  const { auth, language } = useGeneral();
  const { token, user } = auth;
  const { getTranslation } = language;
  const userId = user?.id || '';

  // Fetch user's channels
  const { channels: userChannels, loading: channelsLoading } = useChannels({
    token: token || null,
    userID: userId,
    popular: false
  });

  // Filter and sort admin channels by user count (descending)
  const adminChannels = useMemo(() => {
    const filtered = userChannels.filter(channel => String(channel.isAdmin) === "1");
    return filtered.sort((a, b) => {
      const countA = a.userCount || a.user_count || a.memberCount || 0;
      const countB = b.userCount || b.user_count || b.memberCount || 0;
      return countB - countA; // Descending order (highest first)
    });
  }, [userChannels]);


  const handleChannelClick = (channel: ChannelListItem) => {
    const channelTitle = channel.channelTitle || channel.title;
    if (!channelTitle) return;
    const slug = createSlug(channelTitle);
    navigate(`/communities/${slug}/admin`);
  };

  const handleCreateClick = () => {
    navigate('/communities/create');
  };

  if (channelsLoading) {
    return (
      <div className={styles.adminChannelsPage}>
        <div className={styles.centeredBox}>
          <div className={styles.loadingContainer}>
            {[1, 2, 3, 4].map((i) => (
              <LoadingSkeleton key={i} width="100%" height={72} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminChannelsPage}>
      <div className={styles.centeredBox}>
        {adminChannels.length > 0 && (
          <div className={styles.communitiesList}>
            {adminChannels.map((channel) => {
              const channelTitle = channel.channelTitle || channel.title;
              const channelLogo = channel.channelLogo || channel.logo;
              const hasLogo = !!channelLogo;

              return (
                <AdminChannelListItem
                  key={channel.channelID}
                  channel={channel}
                  channelTitle={channelTitle}
                  channelLogo={channelLogo}
                  hasLogo={hasLogo}
                  onClick={() => handleChannelClick(channel)}
                />
              );
            })}
          </div>
        )}

        {/* Create Community Button - Full Row */}
        <div className={styles.createButtonRow}>
          <div
            className={styles.createListItem}
            onClick={handleCreateClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCreateClick();
              }
            }}
          >
            <div className={styles.listItemLeft}>
              <div className={styles.listItemIcon}>
                <div className={styles.plusIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className={styles.listItemText}>
                <div className={styles.listItemTitle}>
                  {getTranslation('adminChannels.createCommunity') || 'Topluluk Oluştur'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminChannelsPage;

